import { FormHelperText, FormLabel, Option, Select } from "@mui/joy";
import { addMonths, format, parse } from "date-fns";
import { ReactNode, useMemo, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import FormGroup from "./FormGroup";

const VALUE_FORMAT = "yyyy-MM";

/**
 * Picks a calendar month from a single dropdown. The form value is a "yyyy-MM" string, or null
 * while nothing is chosen. A native `type="month"` input would be simpler, but desktop Safari
 * doesn't support it.
 */
export default function MonthInput({
  name,
  label,
  disabled,
  minMonth,
}: {
  name: string;
  label: ReactNode | null;
  disabled?: boolean;
  /** The earliest month on offer, as "yyyy-MM". */
  minMonth?: string;
}) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control });
  const value = (field.value as string | null) ?? null;

  const [today] = useState(() => new Date());

  // From `minMonth` (or the start of last year) to the end of two years ahead, widened to include
  // whatever is already chosen (an existing quest can be from any time).
  const months = useMemo(() => {
    const parseMonth = (month: string) => parse(month, VALUE_FORMAT, today);
    const windowStart = minMonth ? parseMonth(minMonth) : new Date(today.getFullYear() - 1, 0, 1);
    const windowEnd = new Date(today.getFullYear() + 2, 11, 1);
    const selected = value ? parseMonth(value) : null;
    const first = selected && selected < windowStart ? selected : windowStart;
    const last = selected && selected > windowEnd ? selected : windowEnd;

    const result: { value: string; label: string }[] = [];
    for (let month = first; month <= last; month = addMonths(month, 1)) {
      result.push({ value: format(month, VALUE_FORMAT), label: format(month, "MMMM yyyy") });
    }
    return result;
  }, [today, value, minMonth]);

  return (
    <FormGroup error={Boolean(fieldState.error)}>
      {label !== null ? <FormLabel>{label}</FormLabel> : null}
      <Select
        placeholder="Select month"
        disabled={disabled}
        value={value}
        onChange={(_, month) => field.onChange(month)}
        onBlur={field.onBlur}
      >
        {months.map((month) => (
          <Option key={month.value} value={month.value}>
            {month.label}
          </Option>
        ))}
      </Select>
      {fieldState.error ? <FormHelperText>{fieldState.error.message}</FormHelperText> : null}
    </FormGroup>
  );
}
